import type { GeoPoint } from '@citizen-walk/shared-types';

/**
 * 2 点間 (GeoPoint) の地表面距離 (m) を Haversine 公式で算出する pure helper。
 *
 * user 第 12 弾 (GAP-U3 投稿エリア制限) 用に新設。PostForm から呼出し、現在位置と
 * district.postAreaCenter の距離を district.postAreaRadiusM と比較してエリア内外を判定する。
 *
 * 設計判断:
 *   - 平均地球半径 6371008.8 m (WGS84 mean radius、IUGG 標準)。旧 v1 の `lib/geo.ts` も同値。
 *     都市スケール (〜10km) 圏内なら誤差 0.5% 以下 (楕円体補正不要、Phase 2 minimum 範囲十分)
 *   - 入力検証は呼出側責務 (null/undefined は呼出側で early-return)。本関数は数値前提で動く
 *     pure function に徹し、test しやすさを優先する
 *   - 経度 180° / -180° 跨ぎ (日付変更線) も Haversine の Δlon 計算で自然に処理される
 *     (cos / sin が周期的に対称、Δlon=359° と Δlon=-1° は同結果)
 *
 * 旧 v1 との差分:
 *   - 旧は同等の関数を `old/apps/user/src/lib/geo.ts` 内 inline で持っていたが、Phase 5 投稿エリア
 *     制限 UI 自体が v1 では未実装 (REQ-4.1.7 計算式は docs のみ)。v2 では UI gate の前段の純粋計算
 *     を pure function + test で独立化する
 */
export function haversineDistanceM(a: GeoPoint, b: GeoPoint): number {
  // WGS84 mean radius (meters)。Phase 2 minimum 範囲で sphere 近似十分。
  const R = 6371008.8;
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const dLat = lat2 - lat1;
  const dLon = toRad(b.longitude - a.longitude);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  // Math.asin(Math.sqrt(h)) より atan2 版が h≈1 (対蹠点) でも安定する
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
}

/**
 * `location` が `center` を中心とする半径 `radiusM` の円内に含まれるかを判定する。
 *
 * PostForm 投稿エリア制限 UI の primary 判定。`radiusM` を含む (≤) 同心円。
 * `radiusM` が 0 以下・非有限 (NaN/Infinity) の場合は false 扱い (= 投稿不可となるが、
 * district 設定が壊れている異常系なので呼出側で「未設定」と区別して扱うことを推奨)。
 */
export function isWithinArea(location: GeoPoint, center: GeoPoint, radiusM: number): boolean {
  if (!Number.isFinite(radiusM) || radiusM <= 0) return false;
  return haversineDistanceM(location, center) <= radiusM;
}
